import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { useParams } from "react-router-dom";
import BASE_URL from "../config";
import "../styles/page.css";
import parse, { domToReact } from "html-react-parser"; // Import parse and domToReact
import { FaPlus, FaMinus } from "react-icons/fa"; // Import icons
import CareerForm from "../components/CareerForm"; // Import the modular CareerForm component
import CareerToggleTabs from "../components/CareerToggleTabs"; // Import the CareerToggleTabs component
import ContactForm from "../components/ContactForm"; // Import the ContactForm component
import Loader from "../components/Loader"; // Import Loader component

const Page = () => {
  const { pageName } = useParams();
  const [page, setPage] = useState({
    title: "",
    pagename: "",
    content: "",
    meta: "",
    active: 0,
  });
  const [loading, setLoading] = useState(true);
  const [activeToggle, setActiveToggle] = useState(null);
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    position: "",
    attachment: null,
  });
  const [validationErrors, setValidationErrors] = useState({}); // State to track validation errors

  const handleToggle = (toggleId) => {
    setActiveToggle(activeToggle === toggleId ? null : toggleId);
  };

  const handleInputChange = (e) => {
    const { id, value, files } = e.target;
    setFormData({
      ...formData,
      [id]: files ? files[0] : value,
    });

    // Clear validation error for the specific field
    setValidationErrors((prevErrors) => {
      const { [id]: removedError, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  const handleFocus = (fieldId) => {
    setValidationErrors((prevErrors) => {
      const { [fieldId]: removedError, ...remainingErrors } = prevErrors;
      return remainingErrors;
    });
  };

  const validateStep = (isSubmit = false) => {
    const errors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/; // Regular expression for email validation
    const phoneRegex = /^[0-9+()]+$/; // Regular expression for phone number validation

    if (step === 1 || isSubmit) {
      if (!formData.name.trim()) {
        errors.name = "Name is required.";
      }
    }
    if (step === 2 || isSubmit) {
      if (!formData.email.trim()) {
        errors.email = "Email is required.";
      } else if (!emailRegex.test(formData.email)) {
        errors.email = "Invalid email format.";
      }
      if (!formData.phone.trim()) {
        errors.phone = "Phone number is required.";
      } else if (!phoneRegex.test(formData.phone)) {
        errors.phone = "Phone number can only contain numbers.";
      }
    }
    if (step === 3 || isSubmit) {
      if (!formData.position.trim()) {
        errors.position = "Position name is required.";
      }
      if (!formData.attachment) {
        errors.attachment = "Attachment is required.";
      }
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0; // Return true if no errors
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
      setValidationErrors({}); // Clear validation errors
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateStep(true)) { // Validate all fields across all steps
      console.log("Form Data Submitted:", formData);
      setValidationErrors({}); // Clear validation errors
      Swal.fire("Success", "Form submitted successfully!", "success"); // Optional success message
    }
  };

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const response = await axios.get(`${BASE_URL}api/pages/find-by-slug/${pageName}`, {
          headers: { "Content-Type": "application/json" },
        });
        // console.log(response.data);
        if (response.data) {
          setPage(response.data);
          document.title = (response.data.pagename ? `${response.data.pagename} | Softisans` : "Default Title");

          const metaDescription = document.querySelector("meta[name='description']");
          if (metaDescription) {
            metaDescription.setAttribute("content", response.data.meta || "");
          } else {
            const meta = document.createElement("meta");
            meta.name = "description";
            meta.content = response.data.meta || "";
            document.head.appendChild(meta);
          }
        }
      } catch (error) {
        Swal.fire("Error", error.response?.data?.message || "Failed to fetch page", "error");
      } finally {
        setLoading(false);
      }
    };

    if (pageName) {
      fetchContent();
    }
  }, [pageName]);

  const ContactUsButton = ({ className, href, dataIcon }) => {
    return (
      <a
        className={className}
        href={href}
        data-icon={dataIcon}
        onMouseEnter={(e) => {
          e.target.innerHTML = 'Contact Us <span style="display: inline-block; transform: translateX(0); transition: transform 300ms ease;">→</span>';
          const arrow = e.target.querySelector("span");
          setTimeout(() => {
            arrow.style.transform = "translateX(5px)";
          }, 0);
        }}
        onMouseLeave={(e) => {
          e.target.innerHTML = "Contact Us";
        }}
      >
        Contact Us
      </a>
    );
  };

  const parseContent = (content) => {
    return parse(content, {
      replace: (domNode) => {
        if (domNode.type === "text" && domNode.data.includes("[career-form]")) {
          return <CareerForm />;
        }
        if (domNode.type === "text" && domNode.data.includes("[career-toggle-tabs]")) {
          return <CareerToggleTabs />;
        }
        if (domNode.type === "text" && domNode.data.includes("[contact-us-form]")) {
          return <ContactForm />;
        }
        if (domNode.type === "text" && domNode.data.includes("[contact-us-buttons]")) {
          return (
            <ContactUsButton
              className="contact-button-contact-us"
              href="contact-us"
              dataIcon="$"
            />
          );
        }
        if (domNode.name === "script" && domNode.children.length > 0) {
          const scriptContent = domToReact(domNode.children);
          try {
            // eslint-disable-next-line no-eval
            eval(scriptContent);
          } catch (error) {
            console.error("Error evaluating script:", error);
          }
          return null; // Do not render the script tag
        }
      },
    });
  };

  if (!loading && page.active !== true) {
    return (
      <div className="container mt-4">
        <h2>Page Not Active</h2>
        <p>This page is currently inactive. Please check back later.</p>
      </div>
    );
  }

  return (
    <div className="">
      {loading ? (
        <Loader message="Loading Page..." /> 
      ) : (
        <div>
          {parseContent(page.content)}
          </div>
      )}
      
    </div>
  );
};

export default Page;
